import React, { Component } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button
} from "shards-react";
import Select from "react-select";
import PageTitle from "../../components/common/PageTitle";
import { strings } from "../../localization";
import { Language, API_URL } from "../../config";
import swal from "sweetalert2";

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    marginTop: "10px",
    marginBottom: "10px",
  }),
};

class AddressMapping extends Component {
  constructor(props) {
    super(props);
    strings.setLanguage(Language);
    this.state = {
      users: [],
      organisationInfo: null,
      additionalAddresses: [],
      mappings: {}, // addressString -> [user_ids]
      loading: true,
      saving: false,
      addressObjects: []
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  fetchData = async () => {
    try {
      this.setState({ loading: true });

      // Fetch users
      const usersRes = await fetch(`${API_URL}users`, {
        method: "GET",
        credentials: "include"
      });
      const usersData = await usersRes.json();
      const allUsers = usersData.result.map(u => ({
        id: u._id,
        userName: u.data.userName,
        address: u.data.address
      }));

      // Fetch organization and addresses
      const orgInfoStr = localStorage.getItem("organisation_info");
      const orgInfo = JSON.parse(orgInfoStr);

      const orgRes = await fetch(`${API_URL}organisations/${orgInfo._id}`, {
        method: "GET",
        credentials: "include"
      });
      const { data: organisationData } = await orgRes.json();

      const addressObjects = [];
      if (organisationData.address) {
        addressObjects.push({
          address: organisationData.address,
          city: organisationData.city || "",
          state: organisationData.state || "",
          dirOrigen: organisationData.dirOrigen || "",
          cmnaOrigen: organisationData.cmnaOrigen || "",
          siiAddress: organisationData.siiAddress || ""
        });
      }
      if (organisationData.additionalAddresses) {
        organisationData.additionalAddresses.forEach(addr => {
          if (addr.address) {
            addressObjects.push({
              address: addr.address,
              city: addr.city || "",
              state: addr.state || "",
              dirOrigen: addr.dirOrigen || "",
              cmnaOrigen: addr.cmnaOrigen || "",
              siiAddress: addr.siiAddress || ""
            });
          }
        });
      }

      // Initialize mappings based on current user addresses
      const initialMappings = {};
      const addresses = this.getFormattedAddresses(addressObjects);

      // Initialize mappings for each address to an empty array
      addresses.forEach(addr => {
        initialMappings[addr] = [];
      });

      // Fill mappings by checking which addresses are assigned to each user
      allUsers.forEach(u => {
        if (u.address) {
          const userAddresses = u.address.split(" | ");
          userAddresses.forEach(joinedAddr => {
            if (initialMappings[joinedAddr]) {
              initialMappings[joinedAddr].push(u.id);
            }
          });
        }
      });

      this.setState({
        users: allUsers,
        organisationInfo: organisationData,
        additionalAddresses: organisationData.additionalAddresses || [],
        addressObjects,
        mappings: initialMappings,
        loading: false
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      swal.fire("Error", "No se pudo cargar la información", "error");
      this.setState({ loading: false });
    }
  };

  getFormattedAddresses = (objects) => {
    return objects.map(addr => {
      let fullAddr = addr.address;
      if (addr.dirOrigen) fullAddr += ` | ${addr.dirOrigen}`;
      if (addr.cmnaOrigen) fullAddr += ` | ${addr.cmnaOrigen}`;
      if (addr.city) fullAddr += `, ${addr.city}`;
      if (addr.state) fullAddr += `, ${addr.state}`;
      if (addr.siiAddress) fullAddr += ` | ${addr.siiAddress}`;
      return fullAddr;
    });
  };

  handleUserChange = (address, selectedOptions) => {
    const selectedUserIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];

    // Simply update the mapping for this address. 
    // Do NOT remove these users from other addresses (multi-mapping support).
    this.setState(prevState => ({
      mappings: {
        ...prevState.mappings,
        [address]: selectedUserIds
      }
    }));
  };

  handleSave = async () => {
    try {
      this.setState({ saving: true });

      const { mappings, addressObjects } = this.state;
      const formattedAddresses = this.getFormattedAddresses(addressObjects);

      // Construct a payload where each address object also includes its assigned users
      const mappingsPayload = addressObjects.map((addrObj, index) => {
        const addressKey = formattedAddresses[index];
        return {
          ...addrObj,
          users: mappings[addressKey] || []
        };
      });

      // Make a single batch update call with the new payload format
      const response = await fetch(`${API_URL}users/address-mapping`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ data: mappingsPayload }),
        credentials: "include"
      });

      if (!response.ok) {
        throw new Error("Failed to save mapping");
      }

      swal.fire(strings.success, strings.mapped_successfully, "success");
      this.fetchData(); // Refresh data
    } catch (error) {
      console.error("Error saving mappings:", error);
      swal.fire("Error", "No se pudieron guardar los cambios", "error");
    } finally {
      this.setState({ saving: false });
    }
  };

  render() {
    const { users, mappings, loading, saving, addressObjects } = this.state;

    const userOptions = users.map(u => ({
      value: u.id,
      label: u.userName
    }));

    // Generate formatted addresses for keys
    const addressKeys = this.getFormattedAddresses(addressObjects);

    return (
      <Container fluid className="main-content-container px-4">
        <Row noGutters className="page-header py-4">
          <PageTitle
            sm="4"
            title={strings.user_mapping}
            subtitle={strings.overview}
            className="text-sm-left"
          />
        </Row>

        <Row>
          <Col lg="12">
            <Card small className="mb-4">
              <CardHeader className="border-bottom d-flex justify-content-between align-items-center">
                <h6 className="m-0">{strings.mapping_address}</h6>
                <div className="d-flex align-items-center">
                  <Button
                    theme="white"
                    className="mr-2"
                    onClick={() => window.location.replace("/users")}
                    disabled={saving || loading}
                  >
                    {strings.back}
                  </Button>
                  <Button
                    theme="primary"
                    onClick={this.handleSave}
                    disabled={saving || loading}
                  >
                    {saving && <i className="fa fa-spinner fa-spin mr-1" />}
                    {strings.save}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center p-5">
                    <i className="fa fa-spinner fa-spin fa-3x" />
                  </div>
                ) : (
                  <Row>
                    {addressObjects.map((addrObj, index) => {
                      const addressKey = addressKeys[index];
                      const selectedUsers = userOptions.filter(opt =>
                        mappings[addressKey] && mappings[addressKey].includes(opt.value)
                      );

                      return (
                        <Col lg="12" key={index} className="mb-4">
                          <Card
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '10px',
                              boxShadow: 'none'
                            }}
                          >
                            <CardBody className="p-4">
                              <Row className="align-items-center">
                                {/* Left Side: Address Block (Detailed view like Invoice Page) */}
                                <Col sm="5" className="pr-4">
                                  <div className="text-muted" style={{ fontSize: '15px' }}>
                                    <p className="mb-1"><strong style={{ fontWeight: 'bold' }}>{strings.address} :</strong> {addrObj.address || '-'}</p>
                                    <p className="mb-1"><strong style={{ fontWeight: 'bold' }}>{strings.city} :</strong> {addrObj.city || '-'}</p>
                                    <p className="mb-1"><strong style={{ fontWeight: 'bold' }}>{strings.state} :</strong> {addrObj.state || '-'}</p>
                                    {addrObj.dirOrigen && <p className="mb-1"><strong style={{ fontWeight: 'bold' }}>{strings.dir_origen} :</strong> {addrObj.dirOrigen}</p>}
                                    {addrObj.cmnaOrigen && <p className="mb-1"><strong style={{ fontWeight: 'bold' }}>{strings.cmna_origen} :</strong> {addrObj.cmnaOrigen}</p>}
                                    {addrObj.siiAddress && <p className="mb-0"><strong style={{ fontWeight: 'bold' }}>{strings.sii_address} :</strong> {addrObj.siiAddress}</p>}
                                  </div>
                                </Col>

                                {/* Right Side: Users Dropdown */}
                                <Col sm="7" className="pl-4">
                                  <label className="mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', color: '#333' }}>
                                    {strings.assigned_users}
                                  </label>
                                  <Select
                                    isMulti
                                    options={userOptions}
                                    value={selectedUsers}
                                    onChange={(opts) => this.handleUserChange(addressKey, opts)}
                                    styles={customSelectStyles}
                                    placeholder={strings.select_users}
                                  />
                                </Col>
                              </Row>
                            </CardBody>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                )}
                {!loading && addressObjects.length === 0 && (
                  <div className="text-center p-4">
                    <p className="text-muted mb-0">No hay direcciones configuradas en la organización.</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }
}

export default AddressMapping;
